/**
 * Regras puras dos filtros da aba "Meus Estudos" (Flux Personalizado, #1990). Sem React, URL do
 * navegador, sessão ou relógio: quem chama passa os parâmetros, o papel e o dia de hoje em SP.
 *
 * - URL da tela: `q, status (CSV), empresa, ref, periodo, from, to (só com intervalo), criado_por,
 *   page, per_page`. Os presets de período vão relativos (D17, `periodo=semana`).
 * - `parseFilters` valida campo a campo: um valor inválido vira o padrão daquele campo, sem
 *   derrubar os outros e sem lançar exceção.
 * - `serializeFilters` escreve a forma canônica, então `serializeFilters(parseFilters(x)) === x`
 *   para toda URL canônica.
 * - `toQuery` devolve a consulta de DOMÍNIO (`MyStudiesQuery`); os nomes de wire são do adapter da
 *   #1993 (E9). Os nomes da URL da tela coincidem com alguns de wire, mas não passam daqui.
 * - `buildFilterChips` (#1997) monta os chips do resumo; há chip exatamente quando
 *   `hasActiveFilters` é verdadeiro.
 */

import {
  FLUX_STUDY_STATUS,
  FLUX_STUDY_STATUS_CODES,
  FluxStudyStatusCode,
} from 'constants/fluxStudyStatus';
import { isDateOnly } from 'helper/flux/myStudiesAdapter';
import { defaultInterval, periodRange } from 'helper/flux/myStudiesPeriod';
import { formatDateOnly } from 'helper/flux/studyDates';
import {
  DateOnly, MyStudiesCreatorScope, MyStudiesPageSize, MyStudiesQuery,
} from 'types/Flux/Personalized/myStudies';
import {
  MyStudiesCompanyFilter,
  MyStudiesCreatorFilter,
  MyStudiesDateInterval,
  MyStudiesDateRef,
  MyStudiesFilterChip,
  MyStudiesFilterChipLabels,
  MyStudiesFilters,
  MyStudiesFiltersChange,
  MyStudiesPeriod,
} from 'types/Flux/Personalized/myStudiesFilters';

/** Linhas por página aceitas (as mesmas do backend). */
export const MY_STUDIES_PAGE_SIZES: readonly MyStudiesPageSize[] = [10, 20, 50];

const PERIODS: readonly MyStudiesPeriod[] = ['hoje', 'semana', 'mes', 'intervalo', 'tudo'];

const DATE_REFS: readonly MyStudiesDateRef[] = ['last_exec', 'created'];

/** Chaves da URL que pertencem aos filtros, na ordem em que `serializeFilters` as escreve. */
export const MY_STUDIES_FILTER_PARAMS = [
  'q', 'status', 'empresa', 'ref', 'periodo', 'from', 'to', 'criado_por', 'page', 'per_page',
] as const;

/** Inteiro positivo em decimal, sem sinal, zero à esquerda, expoente ou fração. */
const POSITIVE_INTEGER = /^[1-9]\d*$/;

/* ------------------------------------------------------------------------------------ */
/* Leitura campo a campo                                                                */
/* ------------------------------------------------------------------------------------ */

function parsePositiveInteger(value: string | null): number | null {
  if (value === null || !POSITIVE_INTEGER.test(value)) return null;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) ? parsed : null;
}

function isOneOf<T extends string>(options: readonly T[], value: string | null): value is T {
  return value !== null && (options as readonly string[]).includes(value);
}

/** Status válidos, sem repetidos, na ordem de exibição; aceita CSV e chave repetida. */
function canonicalStatus(codes: readonly string[]): FluxStudyStatusCode[] {
  return FLUX_STUDY_STATUS_CODES.filter((code) => codes.includes(code));
}

function parseStatus(values: string[]): FluxStudyStatusCode[] {
  return canonicalStatus(values.flatMap((value) => value.split(',')));
}

function parseCompany(value: string | null): MyStudiesCompanyFilter | null {
  if (value === 'todas') return 'todas';
  const companyId = parsePositiveInteger(value);
  return companyId === null ? null : { companyId };
}

function parseCreator(value: string | null): MyStudiesCreatorFilter | null {
  if (value === 'me' || value === 'empresa') return value;
  const userId = parsePositiveInteger(value);
  return userId === null ? null : { userId };
}

function parsePageSize(value: string | null): MyStudiesPageSize | null {
  return MY_STUDIES_PAGE_SIZES.find((size) => String(size) === value) ?? null;
}

/**
 * Linhas por página aceitas pela tela e pelo backend (a paginação do DS entrega um `number`).
 *
 * @param value - Quantidade escolhida.
 * @returns `true` para 10, 20 ou 50.
 */
export function isMyStudiesPageSize(value: number): value is MyStudiesPageSize {
  return (MY_STUDIES_PAGE_SIZES as readonly number[]).includes(value);
}

/** Intervalo só vale inteiro, com datas reais e `from <= to`. */
function parseInterval(from: string | null, to: string | null): MyStudiesDateInterval | null {
  if (!isDateOnly(from) || !isDateOnly(to) || from > to) return null;
  return { from, to };
}

function sameCompany(a: MyStudiesCompanyFilter, b: MyStudiesCompanyFilter): boolean {
  if (a === 'todas' || b === 'todas') return a === b;
  return a.companyId === b.companyId;
}

/* ------------------------------------------------------------------------------------ */
/* API                                                                                  */
/* ------------------------------------------------------------------------------------ */

/**
 * Filtros com que a tela abre (D4).
 *
 * @param isAdmin - Papel de quem vê a tela.
 * @param todaySP - Hoje em São Paulo (`todayInSaoPaulo`), para o intervalo padrão.
 * @returns Cliente: Criado por "Eu". Admin: Todas as empresas e Criado por Todos (`'empresa'`).
 *   Os dois: Esta semana, última execução, página 1, 20 por página, sem status e sem busca, e o
 *   intervalo {1º dia do mês, hoje} em SP (.dc.html:570-571).
 */
export function defaultFilters(isAdmin: boolean, todaySP: DateOnly): MyStudiesFilters {
  return {
    busca: '',
    status: [],
    empresa: 'todas',
    ref: 'last_exec',
    periodo: 'semana',
    intervalo: defaultInterval(todaySP),
    criadoPor: isAdmin ? 'empresa' : 'me',
    page: 1,
    perPage: 20,
  };
}

/**
 * A URL tem algum parâmetro dos filtros (mesmo inválido)? Sem nenhum, o hook restaura da sessão.
 *
 * @param params - Query string da página.
 */
export function hasFilterParams(params: URLSearchParams): boolean {
  return MY_STUDIES_FILTER_PARAMS.some((key) => params.has(key));
}

/** O que a tela libera na leitura dos filtros. */
export interface ParseFiltersOptions {
  /**
   * Referência "Criação do estudo" liberada (D8). Padrão `true`. Com `false`, `ref=created` vira
   * o padrão (`last_exec`): nenhuma consulta sai com a referência que a tela não deixa escolher.
   */
  createdRefAvailable?: boolean;
}

/**
 * Lê os filtros da URL, validando campo a campo; nunca lança exceção.
 *
 * @param params - Query string da página.
 * @param isAdmin - Papel: define os padrões e se a empresa específica vale (para cliente ela vira
 *   'todas', porque o backend a ignora e a tela não deve mostrar um filtro sem efeito).
 * @param todaySP - Hoje em São Paulo, para o intervalo padrão.
 * @param options - `createdRefAvailable` (D8, #1996).
 * @returns Filtros completos; `from`/`to` só são lidos com `periodo=intervalo`.
 */
export function parseFilters(
  params: URLSearchParams,
  isAdmin: boolean,
  todaySP: DateOnly,
  { createdRefAvailable = true }: ParseFiltersOptions = {},
): MyStudiesFilters {
  const defaults = defaultFilters(isAdmin, todaySP);
  const periodParam = params.get('periodo');
  const refParam = params.get('ref');
  const periodo = isOneOf(PERIODS, periodParam) ? periodParam : defaults.periodo;
  const intervalo = periodo === 'intervalo'
    ? parseInterval(params.get('from'), params.get('to'))
    : null;
  const refAllowed = createdRefAvailable || refParam !== 'created';

  return {
    busca: (params.get('q') ?? '').trim(),
    status: parseStatus(params.getAll('status')),
    empresa: (isAdmin ? parseCompany(params.get('empresa')) : null) ?? defaults.empresa,
    ref: isOneOf(DATE_REFS, refParam) && refAllowed ? refParam : defaults.ref,
    periodo,
    intervalo: intervalo ?? defaults.intervalo,
    criadoPor: parseCreator(params.get('criado_por')) ?? defaults.criadoPor,
    page: parsePositiveInteger(params.get('page')) ?? defaults.page,
    perPage: parsePageSize(params.get('per_page')) ?? defaults.perPage,
  };
}

/**
 * Escreve os filtros na forma canônica da URL (sem o '?').
 *
 * @param filters - Filtros da tela.
 * @returns Ex.: 'status=pronto,erro&empresa=todas&ref=last_exec&periodo=semana&criado_por=me
 *   &page=1&per_page=20' (numa linha só).
 *   `q` e `status` só aparecem com valor; `from`/`to`, só com intervalo; o nome do criador nunca.
 */
export function serializeFilters(filters: MyStudiesFilters): string {
  const params = new URLSearchParams();
  const busca = filters.busca.trim();
  const status = canonicalStatus(filters.status);

  if (busca) params.set('q', busca);
  if (status.length > 0) params.set('status', status.join(','));
  params.set('empresa', filters.empresa === 'todas' ? 'todas' : String(filters.empresa.companyId));
  params.set('ref', filters.ref);
  params.set('periodo', filters.periodo);
  if (filters.periodo === 'intervalo') {
    params.set('from', filters.intervalo.from);
    params.set('to', filters.intervalo.to);
  }
  params.set(
    'criado_por',
    typeof filters.criadoPor === 'string' ? filters.criadoPor : String(filters.criadoPor.userId),
  );
  params.set('page', String(filters.page));
  params.set('per_page', String(filters.perPage));

  // A vírgula do CSV não precisa de escape numa query string; legível na barra de endereço
  return params.toString().replace(/%2C/g, ',');
}

/** A consulta muda? (a página fica de fora; o nome do criador e o intervalo fora de uso, também) */
function queryChanged(before: MyStudiesFilters, after: MyStudiesFilters): boolean {
  return serializeFilters({ ...before, page: 1 }) !== serializeFilters({ ...after, page: 1 });
}

/**
 * Página aceita: inteiro seguro a partir de 1.
 *
 * @param page - Número pedido pela tela.
 */
export function isValidPageNumber(page: number): boolean {
  return Number.isSafeInteger(page) && page >= 1;
}

/** Intervalo com as duas datas reais, em ordem; `null` se uma delas não for `DateOnly` válida. */
function orderedInterval(interval: MyStudiesDateInterval): MyStudiesDateInterval | null {
  const { from, to } = interval;
  if (!isDateOnly(from) || !isDateOnly(to)) return null;
  return from <= to ? { from, to } : { from: to, to: from };
}

/**
 * Só o que pode ser aplicado: sem chaves `undefined`, sem página inválida e com o intervalo
 * ordenado (sem intervalo, se uma data não for válida).
 *
 * @param change - Mudança pedida pela tela.
 * @returns Uma cópia, sem mutar `change`.
 */
export function sanitizeFilterChange(change: MyStudiesFiltersChange): MyStudiesFiltersChange {
  const sanitized = Object.fromEntries(
    Object.entries(change).filter(([, value]) => value !== undefined),
  ) as MyStudiesFiltersChange;

  if (sanitized.page !== undefined && !isValidPageNumber(sanitized.page)) {
    delete sanitized.page;
  }

  if (sanitized.intervalo !== undefined) {
    const intervalo = orderedInterval(sanitized.intervalo);
    if (intervalo) {
      sanitized.intervalo = intervalo;
    } else {
      delete sanitized.intervalo;
    }
  }

  return sanitized;
}

/**
 * Aplica uma mudança pedida pela tela.
 *
 * @param current - Filtros atuais.
 * @param change - Campos que mudam. Passa antes por `sanitizeFilterChange`: chave `undefined` e
 *   página inválida são ignoradas; intervalo invertido é ordenado (10/09 a 01/09 vira 01/09 a
 *   10/09) e intervalo com data inválida é ignorado, sem derrubar o resto da mudança.
 * @returns Novos filtros (sem mutar `current`):
 *   - qualquer mudança que altere a consulta e não traga `page` volta para a página 1 (trocar o
 *     por página inclusive); mudar só a página preserva o resto;
 *   - trocar de empresa estando em um criador específico redefine Criado por para 'empresa'
 *     (.dc.html:666), a menos que a mesma mudança traga o criador; com 'me', mantém.
 */
export function applyFilterChange(
  current: MyStudiesFilters,
  change: MyStudiesFiltersChange,
): MyStudiesFilters {
  const defined = sanitizeFilterChange(change);
  const next: MyStudiesFilters = { ...current, ...defined };

  const companyChanged = defined.empresa !== undefined
    && !sameCompany(defined.empresa, current.empresa);
  if (companyChanged && defined.criadoPor === undefined && typeof current.criadoPor === 'object') {
    next.criadoPor = 'empresa';
  }

  if (defined.page === undefined && queryChanged(current, next)) {
    next.page = 1;
  }

  return next;
}

/**
 * "Limpar filtros" (D6, .dc.html:680).
 *
 * @param current - Filtros atuais.
 * @param todaySP - Hoje em São Paulo, para recompor o intervalo padrão.
 * @returns Período Tudo, Criado por Todos (`'empresa'`), Empresa Todas, sem status e sem busca,
 *   página 1; mantém a referência (`ref`) e o por página.
 */
export function clearFilters(current: MyStudiesFilters, todaySP: DateOnly): MyStudiesFilters {
  return {
    ...current,
    busca: '',
    status: [],
    empresa: 'todas',
    periodo: 'tudo',
    intervalo: defaultInterval(todaySP),
    criadoPor: 'empresa',
    page: 1,
  };
}

/**
 * Há o que limpar? É verdadeiro sempre que "Limpar filtros" mudaria a consulta, inclusive nos
 * padrões (Esta semana; e, para cliente, Criado por "Eu"). É o mesmo que
 * `buildFilterChips(filters).length > 0` (#1997): "Limpar filtros" aparece junto com os chips.
 * Status desconhecido não conta, como na URL (`canonicalStatus`).
 *
 * @param filters - Filtros atuais.
 */
export function hasActiveFilters(filters: MyStudiesFilters): boolean {
  return filters.busca.trim() !== ''
    || canonicalStatus(filters.status).length > 0
    || filters.empresa !== 'todas'
    || filters.periodo !== 'tudo'
    || filters.criadoPor !== 'empresa';
}

/* ------------------------------------------------------------------------------------ */
/* Chips do resumo (#1997)                                                              */
/* ------------------------------------------------------------------------------------ */

/**
 * Empresa da antiga caixa "Casos Ampere" (Ampere Consultoria): id 1 no contrato das rotas novas
 * (`mock/flux/personalized/README.md`, parâmetro `empresa` e lista de empresas).
 */
export const AMPERE_COMPANY_ID = 1;

/** Nome fixo da empresa 1 no filtro Empresa e no chip (nota 3 do design, .dc.html:46). */
export const AMPERE_COMPANY_LABEL = 'Casos Ampere';

/** Rótulo dos presets no chip (.dc.html:569); o intervalo é formatado à parte. */
const PERIOD_CHIP_LABELS: Readonly<Record<Exclude<MyStudiesPeriod, 'intervalo' | 'tudo'>, string>> = {
  hoje: 'Hoje',
  semana: 'Esta semana',
  mes: 'Este mês',
};

/** Sufixo do chip de período: repete a data de referência (D04, .dc.html:674). */
const DATE_REF_CHIP_SUFFIX: Readonly<Record<MyStudiesDateRef, string>> = {
  last_exec: 'últ. execução',
  created: 'criação',
};

/**
 * Intervalo no chip (.dc.html:671): '01/10 – 07/10/2026'. A ponta inicial não repete o ano,
 * como no protótipo, só quando é o mesmo da final; se o intervalo cruza o ano, as duas pontas
 * levam o ano ('20/12/2025 – 05/01/2026'), porque '20/12 – 05/01/2026' leria como 20/12/2026.
 */
function formatIntervalLabel({ from, to }: MyStudiesDateInterval): string {
  const fromLabel = formatDateOnly(from);
  const toLabel = formatDateOnly(to);
  const sameYear = from.slice(0, 4) === to.slice(0, 4);
  return `${sameYear ? fromLabel.slice(0, 5) : fromLabel} – ${toLabel}`;
}

/**
 * Chip do período (.dc.html:674): preset ou intervalo, seguido da referência.
 *
 * @param filters - Filtros da tela.
 * @returns Ex.: 'Esta semana · últ. execução'; remover vai para Tudo. `null` em "Tudo".
 */
function periodChip(filters: MyStudiesFilters): MyStudiesFilterChip | null {
  const { periodo, ref } = filters;
  if (periodo === 'tudo') return null;
  const period = periodo === 'intervalo'
    ? formatIntervalLabel(filters.intervalo)
    : PERIOD_CHIP_LABELS[periodo];
  return {
    key: 'periodo',
    label: `${period} · ${DATE_REF_CHIP_SUFFIX[ref]}`,
    change: { periodo: 'tudo' },
  };
}

/**
 * Chip de Criado por (.dc.html:675-676).
 *
 * @param criadoPor - Filtro de criador.
 * @param userLabel - Nome do usuário específico vindo da tela (#2000).
 * @returns 'Criado por: meus estudos' ou 'Criado por: <nome>' (`userLabel`, depois o `nome`
 *   guardado, depois 'usuário #<id>'); remover vai para Todos ('empresa'). `null` em Todos.
 */
function creatorChip(
  criadoPor: MyStudiesCreatorFilter,
  userLabel: string | undefined,
): MyStudiesFilterChip | null {
  if (criadoPor === 'empresa') return null;
  const name = criadoPor === 'me'
    ? 'meus estudos'
    : userLabel?.trim() || criadoPor.nome?.trim() || `usuário #${criadoPor.userId}`;
  return { key: 'criadoPor', label: `Criado por: ${name}`, change: { criadoPor: 'empresa' } };
}

/**
 * Chip da empresa (.dc.html:677).
 *
 * @param filters - Filtros da tela (o criador vai junto na mudança de remover).
 * @param companyLabel - Nome da empresa vindo da tela (#2000); ignorado para a empresa 1.
 * @returns 'Empresa: Casos Ampere' (empresa 1) ou 'Empresa: <nome>' ('#<id>' sem nome); remover
 *   vai para Todas e mantém o criador. `null` em Todas.
 */
function companyChip(
  filters: MyStudiesFilters,
  companyLabel: string | undefined,
): MyStudiesFilterChip | null {
  const { empresa, criadoPor } = filters;
  if (empresa === 'todas') return null;
  const name = empresa.companyId === AMPERE_COMPANY_ID
    ? AMPERE_COMPANY_LABEL
    : companyLabel?.trim() || `#${empresa.companyId}`;
  // O criador vai junto: sem ele, `applyFilterChange` trocaria um usuário específico por Todos, e
  // o chip "Criado por" sumiria junto (.dc.html:677 só muda a empresa)
  const keptCreator = typeof criadoPor === 'object' ? { ...criadoPor } : criadoPor;
  return {
    key: 'empresa',
    label: `Empresa: ${name}`,
    change: { empresa: 'todas', criadoPor: keptCreator },
  };
}

/**
 * Um chip por status (.dc.html:678).
 *
 * @param status - Status já canônicos (`canonicalStatus`): sem repetidos nem códigos
 *   desconhecidos, para as keys não se repetirem e o rótulo existir.
 * @returns 'Status: <rótulo da D25>'; remover tira só aquele status.
 */
function statusChips(status: FluxStudyStatusCode[]): MyStudiesFilterChip[] {
  return status.map((code) => ({
    key: `status:${code}`,
    label: `Status: ${FLUX_STUDY_STATUS[code].label}`,
    change: { status: status.filter((other) => other !== code) },
  }));
}

/**
 * Chip da busca (.dc.html:679).
 *
 * @param busca - Texto como digitado (o rascunho do campo).
 * @returns 'Nome contém “<texto aparado>”'; remover deixa a busca vazia. `null` se o texto for
 *   vazio ou só espaços.
 */
function searchChip(busca: string): MyStudiesFilterChip | null {
  const text = busca.trim();
  if (text === '') return null;
  return { key: 'busca', label: `Nome contém “${text}”`, change: { busca: '' } };
}

/**
 * Chips removíveis do resumo da lista (#1997, .dc.html:673-679), na ordem do protótipo:
 * período, criado por, empresa, um por status e busca.
 *
 * Há chip exatamente quando `hasActiveFilters(filters)` é verdadeiro (o `hasChips` do protótipo,
 * .dc.html:722): "Limpar filtros" aparece junto com o primeiro chip. Por isso o papel não entra:
 * para cliente, `parseFilters` já devolve sempre Empresa Todas (o backend ignora a empresa), e o
 * chip de empresa nunca aparece para ele.
 *
 * A busca do chip é o texto DIGITADO, como no protótipo, que monta os chips a partir do rascunho:
 * quem chama passa `{ ...filters, busca: searchDraft }` (o `FilterSummary` já faz isso).
 *
 * @param filters - Filtros da tela, com a busca como digitada.
 * @param chipLabels - Nomes de empresa e de usuário que os filtros não guardam (#2000). Sem eles,
 *   o chip mostra o id ('Empresa: #42', 'Criado por: usuário #9') até o nome chegar.
 * @returns Chips; `change` de cada um é o efeito de removê-lo: período → Tudo; criado por → Todos
 *   ('empresa'); empresa → Todas, mantendo o criador; status → tira só aquele; busca → vazia.
 *   Status repetido vira um chip só, e código desconhecido não gera chip (`canonicalStatus`).
 */
export function buildFilterChips(
  filters: MyStudiesFilters,
  { companyLabel, userLabel }: MyStudiesFilterChipLabels = {},
): MyStudiesFilterChip[] {
  const single = [
    periodChip(filters),
    creatorChip(filters.criadoPor, userLabel),
    companyChip(filters, companyLabel),
  ].filter((chip): chip is MyStudiesFilterChip => chip !== null);
  const search = searchChip(filters.busca);

  return [
    ...single,
    ...statusChips(canonicalStatus(filters.status)),
    ...(search ? [search] : []),
  ];
}

function toCreatedBy(criadoPor: MyStudiesCreatorFilter): MyStudiesCreatorScope {
  if (criadoPor === 'me') return 'me';
  if (criadoPor === 'empresa') return 'all';
  return { userId: criadoPor.userId };
}

/**
 * Consulta de domínio da lista (o adapter da #1993 a transforma em parâmetros de wire).
 *
 * @param filters - Filtros aplicados (a busca já passou pelo debounce).
 * @param now - Agora (`new Date()` a cada uso): os presets são relativos ao dia de hoje em SP.
 * @returns `MyStudiesQuery` com o período em dias civis de SP (`null` em "Tudo").
 */
export function toQuery(filters: MyStudiesFilters, now: Date): MyStudiesQuery {
  const range = periodRange(filters.periodo, filters.intervalo, now);

  return {
    pageNumber: filters.page,
    pageSize: filters.perPage,
    dateReference: filters.ref === 'created' ? 'creation' : 'lastExecution',
    dateRange: range ? { start: range.from, end: range.to } : null,
    company: filters.empresa === 'todas' ? 'all' : { companyId: filters.empresa.companyId },
    createdBy: toCreatedBy(filters.criadoPor),
    statuses: [...filters.status],
    search: filters.busca.trim(),
  };
}
